/**
 * scatter.ts — instanced Mediterranean vegetation, rocks, and grass.
 *
 * Species, weights, and heights follow the canonical biome table
 * (biomes.json, Italy/Mediterranean set):
 *   umbrella pine 0.22 (12-20 m), cypress 0.14 (10-18 m),
 *   holm oak 0.30 (8-16 m), olive 0.20 (4-8 m), sweet chestnut 0.14 (10-20 m).
 *
 * No Unreal GLBs are available (the repo's exports are LFS pointers with no
 * objects on the server), so every species is procedural low-poly geometry
 * tuned to read correctly at RTS camera distance: one InstancedMesh per
 * species, vertex-colored, merged trunk+canopy. Placement is a seeded
 * jittered grid over the sim Heightfield's woodland/moisture masks, with
 * river ribbons and field plots excluded. Grass is a camera-following patch
 * of instanced tufts with wind sway; density fades with the ground masks.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { Heightfield, Rng, clamp01, hashSeedString } from "../../sim/pcg";

export interface SpeciesDef {
  id: string;
  weight: number;
  heightMin: number;
  heightMax: number;
  build: () => THREE.BufferGeometry;
}

const _col = new THREE.Color();

function paint(geo: THREE.BufferGeometry, hex: number, jitter = 0.06, seed = 1): THREE.BufferGeometry {
  _col.setHex(hex);
  const n = geo.getAttribute("position").count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    let h = Math.imul(i + seed * 7919, 0x27d4eb2d);
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
    const j = (((h >>> 0) % 1000) / 1000 - 0.5) * jitter;
    colors[i * 3] = Math.max(0, _col.r + j);
    colors[i * 3 + 1] = Math.max(0, _col.g + j);
    colors[i * 3 + 2] = Math.max(0, _col.b + j);
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

/** Displace vertices along their normals for an organic canopy. */
function roughen(geo: THREE.BufferGeometry, amt: number, seed: number): THREE.BufferGeometry {
  const pos = geo.getAttribute("position");
  const nor = geo.getAttribute("normal");
  for (let i = 0; i < pos.count; i++) {
    let h = Math.imul(i + seed * 104729, 0x165667b1);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    const d = (((h >>> 0) % 1000) / 1000 - 0.5) * amt;
    pos.setXYZ(i,
      pos.getX(i) + nor.getX(i) * d,
      pos.getY(i) + nor.getY(i) * d,
      pos.getZ(i) + nor.getZ(i) * d);
  }
  geo.computeVertexNormals();
  return geo;
}

function trunk(h: number, r: number, hex = 0x5a4632, seed = 2): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r * 0.72, r, h, 6, 2);
  g.translate(0, h / 2, 0);
  // Slight organic bend.
  const pos = g.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    pos.setX(i, pos.getX(i) + (y / h) * (y / h) * r * 1.4);
  }
  g.computeVertexNormals();
  return paint(roughen(g, r * 0.25, seed), hex, 0.05, seed);
}

function blob(rx: number, ry: number, rz: number, x: number, y: number, z: number,
  hex: number, seed: number, detail = 1): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, detail);
  g.scale(rx, ry, rz);
  roughen(g, Math.min(rx, ry, rz) * 0.22, seed);
  g.translate(x, y, z);
  return paint(g, hex, 0.09, seed);
}

/** Merge parts, normalizing indexed/non-indexed (Cylinder vs Icosahedron). */
function mergeParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const norm = parts.map((g) => (g.index ? g.toNonIndexed() : g));
  const merged = mergeGeometries(norm, false);
  if (!merged) throw new Error("mergeParts: inconsistent geometry attributes");
  return merged;
}

function buildUmbrellaPine(): THREE.BufferGeometry {
  return mergeParts([
    trunk(7.2, 0.34, 0x5a4632, 11),
    blob(4.4, 1.15, 4.4, 0.4, 8.1, 0, 0x2e4a26, 12, 2),
    blob(2.6, 0.8, 2.6, -1.6, 7.7, 1.2, 0x33522b, 13, 1),
  ]);
}

function buildCypress(): THREE.BufferGeometry {
  const cone = new THREE.ConeGeometry(1.15, 9.5, 7, 3);
  roughen(cone, 0.16, 21);
  cone.translate(0, 5.6, 0);
  return mergeParts([
    trunk(1.2, 0.22, 0x4e3d2c, 22),
    paint(cone, 0x243d22, 0.08, 23),
  ]);
}

function buildHolmOak(): THREE.BufferGeometry {
  return mergeParts([
    trunk(3.1, 0.4, 0x54402e, 31),
    blob(2.9, 2.2, 2.9, 0, 5.2, 0, 0x3a5228, 32, 2),
    blob(2.0, 1.6, 2.0, 1.8, 4.4, 0.8, 0x425c2c, 33, 1),
    blob(1.7, 1.4, 1.7, -1.6, 4.6, -1.0, 0x34491f, 34, 1),
  ]);
}

function buildOlive(): THREE.BufferGeometry {
  // Short gnarled trunk: two offset segments.
  const t1 = new THREE.CylinderGeometry(0.2, 0.34, 1.5, 6, 2);
  t1.translate(0, 0.75, 0);
  const t2 = new THREE.CylinderGeometry(0.14, 0.2, 1.1, 6, 1);
  t2.rotateZ(0.5);
  t2.translate(0.35, 1.8, 0);
  const trunkG = mergeParts([paint(roughen(t1, 0.1, 41), 0x6a5c48, 0.06, 41),
    paint(roughen(t2, 0.08, 42), 0x74644e, 0.06, 42)]);
  return mergeParts([
    trunkG,
    blob(2.0, 1.0, 2.0, 0.5, 2.9, 0, 0x6f7d5a, 43, 1), // silvery-green
    blob(1.3, 0.7, 1.3, -0.9, 2.5, 0.7, 0x7d8a62, 44, 1),
  ]);
}

function buildSweetChestnut(): THREE.BufferGeometry {
  return mergeParts([
    trunk(4.2, 0.44, 0x4e3a2a, 51),
    blob(3.4, 2.6, 3.4, 0, 6.4, 0, 0x41602e, 52, 2),
    blob(2.2, 1.7, 2.2, 2.0, 5.6, 1.2, 0x4a6a33, 53, 1),
  ]);
}

export const ITALY_TREES: SpeciesDef[] = [
  { id: "umbrella_pine", weight: 0.22, heightMin: 12, heightMax: 20, build: buildUmbrellaPine },
  { id: "cypress", weight: 0.14, heightMin: 10, heightMax: 18, build: buildCypress },
  { id: "holm_oak", weight: 0.3, heightMin: 8, heightMax: 16, build: buildHolmOak },
  { id: "olive", weight: 0.2, heightMin: 4, heightMax: 8, build: buildOlive },
  { id: "sweet_chestnut", weight: 0.14, heightMin: 10, heightMax: 20, build: buildSweetChestnut },
];

function buildShrub(): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(0.75, 1);
  g.scale(1.15, 0.7, 1.15);
  roughen(g, 0.16, 61);
  g.translate(0, 0.42, 0);
  return paint(g, 0x44582a, 0.12, 62);
}

function buildRock(): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, 1);
  roughen(g, 0.34, 71);
  // Bedded limestone: squash + strata step.
  const pos = g.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    pos.setY(i, y * 0.62 + Math.round(y * 2.2) * 0.06);
  }
  g.computeVertexNormals();
  return paint(g, 0x8a857a, 0.1, 72);
}

function buildGrassTuft(): THREE.BufferGeometry {
  // Two crossed blade-quads, vertex-colored dark base -> straw tip.
  // Kept small: at RTS distance grass reads as ground texture + sparse tufts.
  const quads: THREE.BufferGeometry[] = [];
  for (let k = 0; k < 2; k++) {
    const g = new THREE.PlaneGeometry(0.8, 0.6, 1, 2);
    g.translate(0, 0.3, 0);
    g.rotateY((k * Math.PI) / 2 + 0.4);
    // Taper top vertices inward for a blade look.
    const pos = g.getAttribute("position");
    const colors = new Float32Array(pos.count * 3);
    _col.setHex(0x5c6a36);
    const tip = new THREE.Color(0xa89a58);
    const tmp = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      if (y > 0.45) pos.setX(i, pos.getX(i) * 0.35);
      tmp.copy(_col).lerp(tip, clamp01(y / 0.6));
      colors[i * 3] = tmp.r; colors[i * 3 + 1] = tmp.g; colors[i * 3 + 2] = tmp.b;
    }
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    quads.push(g);
  }
  return mergeParts(quads);
}

// ---------------------------------------------------------------- placement

const TREE_CELL = 34;
const SHRUB_CELL = 13;
const ROCK_CELL = 46;

export interface ScatterCounts {
  trees: number;
  shrubs: number;
  rocks: number;
  grass: number;
  groveTrees: number;
}

export interface Scatter {
  group: THREE.Group;
  counts: ScatterCounts;
  update(camera: THREE.Camera, elapsed: number): void;
  dispose(): void;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _e = new THREE.Euler();

function grassDensityAt(field: Heightfield, x: number, z: number): number {
  if (field.riverDistAt(x, z) < 9) return 0;
  if (field.slopeAt(x, z) > 0.5) return 0;
  const plot = field.fieldAt(x, z);
  if (plot && plot.crop !== "wheat") return 0;
  const w = field.woodlandAt(x, z);
  const m = field.moistureAt(x, z);
  let d = 0.62 * (1 - w * 0.75) * (0.45 + 0.55 * m);
  if (plot) d *= 0.5;
  return clamp01(d);
}

export function buildScatter(field: Heightfield, seedSuffix = "scatter"): Scatter {
  const group = new THREE.Group();
  const rng = new Rng(hashSeedString(field.seed + ":" + seedSuffix));
  const half = field.half;
  const counts: ScatterCounts = { trees: 0, shrubs: 0, rocks: 0, grass: 0, groveTrees: 0 };

  const treeMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1 });
  const weights = ITALY_TREES.map((t) => t.weight);

  // ---- trees: one InstancedMesh per species
  const perSpecies: { def: SpeciesDef; mats: THREE.Matrix4[] }[] =
    ITALY_TREES.map((def) => ({ def, mats: [] }));

  const placeTree = (x: number, z: number, defIdx: number, scaleMul = 1) => {
    const def = ITALY_TREES[defIdx];
    const h = rng.range(def.heightMin, def.heightMax) * scaleMul;
    _p.set(x, field.heightAt(x, z) - 0.25, z);
    _e.set(0, rng.range(0, Math.PI * 2), 0);
    _q.setFromEuler(_e);
    const s = (h / 10) * rng.range(0.85, 1.15);
    _s.set(s, s * rng.range(0.92, 1.08), s);
    perSpecies[defIdx].mats.push(new THREE.Matrix4().compose(_p, _q, _s));
  };

  for (let gz = 0; gz * TREE_CELL < field.size; gz++) {
    for (let gx = 0; gx * TREE_CELL < field.size; gx++) {
      const x = -half + (gx + 0.5) * TREE_CELL + rng.range(-12, 12);
      const z = -half + (gz + 0.5) * TREE_CELL + rng.range(-12, 12);
      if (Math.abs(x) > half || Math.abs(z) > half) continue;
      if (field.riverDistAt(x, z) < 12) continue;
      if (field.slopeAt(x, z) > 0.55) continue;
      if (field.fieldAt(x, z)) continue;
      const w = field.woodlandAt(x, z);
      if (rng.uniform() > w * 0.85 + 0.015) continue;
      placeTree(x, z, rng.weightedIndex(weights));
      counts.trees++;
    }
  }

  // ---- olive groves: planted rows inside olive field plots
  const oliveIdx = ITALY_TREES.findIndex((t) => t.id === "olive");
  for (const plot of field.fields) {
    if (plot.crop !== "olives") continue;
    const c = Math.cos(plot.rot), s = Math.sin(plot.rot);
    for (let lx = -plot.w / 2 + 4; lx < plot.w / 2 - 3; lx += 7) {
      for (let lz = -plot.d / 2 + 4; lz < plot.d / 2 - 3; lz += 7) {
        const x = plot.cx + lx * c - lz * s + rng.range(-1, 1);
        const z = plot.cz + lx * s + lz * c + rng.range(-1, 1);
        placeTree(x, z, oliveIdx, 0.9);
        counts.groveTrees++;
      }
    }
  }

  for (const { def, mats } of perSpecies) {
    if (!mats.length) continue;
    const geo = def.build();
    const im = new THREE.InstancedMesh(geo, treeMat, mats.length);
    mats.forEach((mtx, i) => im.setMatrixAt(i, mtx));
    im.instanceMatrix.needsUpdate = true;
    im.frustumCulled = false;
    group.add(im);
  }

  // ---- shrubs
  {
    const geo = buildShrub();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1 });
    const mats: THREE.Matrix4[] = [];
    for (let gz = 0; gz * SHRUB_CELL < field.size; gz++) {
      for (let gx = 0; gx * SHRUB_CELL < field.size; gx++) {
        const x = -half + (gx + 0.5) * SHRUB_CELL + rng.range(-5, 5);
        const z = -half + (gz + 0.5) * SHRUB_CELL + rng.range(-5, 5);
        if (Math.abs(x) > half || Math.abs(z) > half) continue;
        if (field.riverDistAt(x, z) < 9) continue;
        if (field.fieldAt(x, z)) continue;
        const w = field.woodlandAt(x, z);
        if (rng.uniform() > 0.06 + w * 0.3) continue;
        _p.set(x, field.heightAt(x, z) - 0.05, z);
        _e.set(0, rng.range(0, Math.PI * 2), 0);
        _q.setFromEuler(_e);
        const s = rng.range(0.7, 1.6);
        _s.set(s, s * rng.range(0.7, 1), s);
        mats.push(new THREE.Matrix4().compose(_p, _q, _s));
      }
    }
    const im = new THREE.InstancedMesh(geo, mat, Math.max(1, mats.length));
    mats.forEach((mtx, i) => im.setMatrixAt(i, mtx));
    im.count = mats.length;
    im.instanceMatrix.needsUpdate = true;
    im.frustumCulled = false;
    group.add(im);
    counts.shrubs = mats.length;
  }

  // ---- rocks (limestone shelves on steeps / stony bands)
  {
    const geo = buildRock();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95 });
    const mats: THREE.Matrix4[] = [];
    for (let gz = 0; gz * ROCK_CELL < field.size; gz++) {
      for (let gx = 0; gx * ROCK_CELL < field.size; gx++) {
        const x = -half + (gx + 0.5) * ROCK_CELL + rng.range(-18, 18);
        const z = -half + (gz + 0.5) * ROCK_CELL + rng.range(-18, 18);
        if (Math.abs(x) > half || Math.abs(z) > half) continue;
        if (field.riverDistAt(x, z) < 7) continue;
        const slope = field.slopeAt(x, z);
        const stony = clamp01((slope - 0.3) * 3);
        if (rng.uniform() > 0.015 + stony * 0.35) continue;
        _p.set(x, field.heightAt(x, z) - 0.15, z);
        _e.set(rng.range(-0.15, 0.15), rng.range(0, Math.PI * 2), rng.range(-0.15, 0.15));
        _q.setFromEuler(_e);
        const s = rng.range(0.5, 2.6);
        _s.set(s * rng.range(0.8, 1.4), s * rng.range(0.35, 0.7), s * rng.range(0.8, 1.4));
        mats.push(new THREE.Matrix4().compose(_p, _q, _s));
      }
    }
    const im = new THREE.InstancedMesh(geo, mat, Math.max(1, mats.length));
    mats.forEach((mtx, i) => im.setMatrixAt(i, mtx));
    im.count = mats.length;
    im.instanceMatrix.needsUpdate = true;
    im.frustumCulled = false;
    group.add(im);
    counts.rocks = mats.length;
  }

  // ---- grass: camera-following patch of instanced tufts
  const GRASS_PATCH = 260;
  const GRASS_CELL = 2;
  const GN = Math.floor(GRASS_PATCH / GRASS_CELL); // 130
  const grassGeo = buildGrassTuft();
  const grassUniforms = { uTime: { value: 0 } };
  const grassMat = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 1, side: THREE.DoubleSide,
  });
  grassMat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = grassUniforms.uTime;
    shader.vertexShader = "uniform float uTime;\n" + shader.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>
      #ifdef USE_INSTANCING
        float gphase = float(gl_InstanceID) * 1.71;
        float gsway = sin(uTime * 1.7 + gphase) * 0.09 * smoothstep(0.0, 0.75, position.y);
        transformed.x += gsway;
        transformed.z += gsway * 0.6;
      #endif`,
    );
  };
  const grassIM = new THREE.InstancedMesh(grassGeo, grassMat, GN * GN);
  grassIM.frustumCulled = false;
  grassIM.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(grassIM);
  counts.grass = GN * GN;

  const grassOrigin = { x: 1e9, z: 1e9 };
  const updateGrass = (camX: number, camZ: number) => {
    const ox = Math.floor(camX / GRASS_CELL) * GRASS_CELL - GRASS_PATCH / 2;
    const oz = Math.floor(camZ / GRASS_CELL) * GRASS_CELL - GRASS_PATCH / 2;
    if (Math.abs(ox - grassOrigin.x) < GRASS_CELL && Math.abs(oz - grassOrigin.z) < GRASS_CELL) return;
    grassOrigin.x = ox; grassOrigin.z = oz;
    let i = 0;
    for (let j = 0; j < GN; j++) {
      for (let k = 0; k < GN; k++, i++) {
        // Deterministic per world cell: stable pattern, no swimming.
        const wx = ox + k * GRASS_CELL;
        const wz = oz + j * GRASS_CELL;
        let h = Math.imul(Math.floor(wx * 0.5) + 0x9e37, 0x27d4eb2d) ^
          Math.imul(Math.floor(wz * 0.5) + 0x51f3, 0x165667b1);
        h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
        const r = ((h >>> 0) % 10000) / 10000;
        const d = grassDensityAt(field, wx, wz);
        // Feather the patch rim so the follow window has no visible edge.
        const ex = Math.min(k, GN - 1 - k) * GRASS_CELL;
        const ez = Math.min(j, GN - 1 - j) * GRASS_CELL;
        const rim = clamp01(Math.min(ex, ez) / 24);
        const on = r < d * rim ? 1 : 0;
        const s = on * (0.45 + r * 0.75);
        _p.set(wx + (r - 0.5) * 1.2, field.heightAt(wx, wz) - 0.03, wz + (((h >>> 13) % 1000) / 1000 - 0.5) * 1.2);
        _e.set(0, r * Math.PI * 2, 0);
        _q.setFromEuler(_e);
        _s.set(s, s * (0.8 + r * 0.5), s);
        grassIM.setMatrixAt(i, _m.compose(_p, _q, _s));
      }
    }
    grassIM.instanceMatrix.needsUpdate = true;
  };

  return {
    group,
    counts,
    update(camera: THREE.Camera, elapsed: number) {
      updateGrass(camera.position.x, camera.position.z);
      grassUniforms.uTime.value = elapsed;
    },
    dispose() {
      group.traverse((o) => {
        const m = o as THREE.InstancedMesh;
        if (m.isInstancedMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
      });
      group.clear();
    },
  };
}
